import type { App } from "@open-booking/server";
import type { CancelBookingPayload, CancellationDecisionPayload, CreateBookingPayload, RefuseBookingPayload } from "@open-booking/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import { type EdenCall, edenBaseQuery } from "@/src/common/lib/eden-base-query";
import { tenantApi } from "@/src/owner/services/tenant-http.service";

type GetBookingsResponse = App["~Routes"]["bookings"]["get"]["response"]["200"];
type GetBookingResponse = App["~Routes"]["bookings"][":id"]["get"]["response"]["200"];
type CreateBookingResponse = App["~Routes"]["bookings"]["post"]["response"]["200"];
type CancelBookingResponse = App["~Routes"]["bookings"][":id"]["cancel"]["patch"]["response"]["200"];
type CancellationDecisionResponse = App["~Routes"]["bookings"][":id"]["cancellation-decision"]["post"]["response"]["200"];
type DeleteBookingResponse = App["~Routes"]["bookings"][":id"]["delete"]["response"]["200"];
type ResendSignatureResponse = App["~Routes"]["bookings"][":id"]["resend-signature"]["post"]["response"]["200"];
type ResendSignedContractResponse = App["~Routes"]["bookings"][":id"]["resend-signed-contract"]["post"]["response"]["200"];
type SendDepositLinkResponse = App["~Routes"]["bookings"][":id"]["send-deposit-link"]["post"]["response"]["200"];
type SendRemainderLinkResponse = App["~Routes"]["bookings"][":id"]["send-remainder-link"]["post"]["response"]["200"];
type AcceptBookingResponse = App["~Routes"]["bookings"][":id"]["accept"]["post"]["response"]["200"];
type RefuseBookingResponse = App["~Routes"]["bookings"][":id"]["refuse"]["post"]["response"]["200"];

export const bookingApi = createApi({
	reducerPath: "bookingApi",
	baseQuery: edenBaseQuery(),
	tagTypes: ["Booking", "Tenant"],
	endpoints: (builder) => ({
		// biome-ignore lint/suspicious/noConfusingVoidType: RTK Query uses `| void` so the hook accepts no argument
		getBookings: builder.query<GetBookingsResponse, { search?: string } | void>({
			query:
				(params): EdenCall<GetBookingsResponse> =>
				(client) =>
					client.bookings.get({ query: params ?? {} }),
			providesTags: ["Booking"],
		}),
		getBooking: builder.query<GetBookingResponse, string>({
			query:
				(id): EdenCall<GetBookingResponse> =>
				(client) =>
					client.bookings({ id }).get(),
			providesTags: ["Booking"],
		}),
		createBooking: builder.mutation<CreateBookingResponse, CreateBookingPayload>({
			query:
				(payload): EdenCall<CreateBookingResponse> =>
				(client) =>
					client.bookings.post(payload),
			invalidatesTags: ["Booking", "Tenant"],
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					// Cross-slice invalidation: refetch the tenant lists/details after a booking is created.
					dispatch(tenantApi.util.invalidateTags(["Tenant"]));
				} catch {
					// no-op: cache stays untouched on error
				}
			},
		}),
		cancelBooking: builder.mutation<CancelBookingResponse, { id: string; data: CancelBookingPayload }>({
			query:
				({ id, data }): EdenCall<CancelBookingResponse> =>
				(client) =>
					client.bookings({ id }).cancel.patch(data),
			invalidatesTags: ["Booking"],
		}),
		cancellationDecision: builder.mutation<CancellationDecisionResponse, { id: string; data: CancellationDecisionPayload }>({
			query:
				({ id, data }): EdenCall<CancellationDecisionResponse> =>
				(client) =>
					client.bookings({ id })["cancellation-decision"].post(data),
			invalidatesTags: ["Booking"],
		}),
		deleteBooking: builder.mutation<DeleteBookingResponse, string>({
			query:
				(id): EdenCall<DeleteBookingResponse> =>
				(client) =>
					client.bookings({ id }).delete(),
			invalidatesTags: ["Booking", "Tenant"],
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					dispatch(tenantApi.util.invalidateTags(["Tenant"]));
				} catch {
					// no-op: cache stays untouched on error
				}
			},
		}),
		resendSignature: builder.mutation<ResendSignatureResponse, string>({
			query:
				(id): EdenCall<ResendSignatureResponse> =>
				(client) =>
					client.bookings({ id })["resend-signature"].post(),
		}),
		resendSignedContract: builder.mutation<ResendSignedContractResponse, string>({
			query:
				(id): EdenCall<ResendSignedContractResponse> =>
				(client) =>
					client.bookings({ id })["resend-signed-contract"].post(),
		}),
		sendDepositLink: builder.mutation<SendDepositLinkResponse, string>({
			query:
				(id): EdenCall<SendDepositLinkResponse> =>
				(client) =>
					client.bookings({ id })["send-deposit-link"].post(),
		}),
		sendRemainderLink: builder.mutation<SendRemainderLinkResponse, string>({
			query:
				(id): EdenCall<SendRemainderLinkResponse> =>
				(client) =>
					client.bookings({ id })["send-remainder-link"].post(),
		}),
		acceptBooking: builder.mutation<AcceptBookingResponse, string>({
			query:
				(id): EdenCall<AcceptBookingResponse> =>
				(client) =>
					client.bookings({ id }).accept.post(),
			invalidatesTags: ["Booking"],
		}),
		refuseBooking: builder.mutation<RefuseBookingResponse, { id: string; data: RefuseBookingPayload }>({
			query:
				({ id, data }): EdenCall<RefuseBookingResponse> =>
				(client) =>
					client.bookings({ id }).refuse.post(data),
			invalidatesTags: ["Booking"],
		}),
	}),
});

export const {
	useGetBookingsQuery,
	useGetBookingQuery,
	useCreateBookingMutation,
	useCancelBookingMutation,
	useCancellationDecisionMutation,
	useDeleteBookingMutation,
	useResendSignatureMutation,
	useResendSignedContractMutation,
	useSendDepositLinkMutation,
	useSendRemainderLinkMutation,
	useAcceptBookingMutation,
	useRefuseBookingMutation,
} = bookingApi;
